import {
  Configuration,
  TurnstileApi,
  DashboardApi,
  DeferredMembershipsApi,
  EventApi,
  EventRegisterApi,
  FormApi,
  GiftCardsApi,
  PaymentsApi,
  PublicEmbedsApi,
  ResponseError,
  RosterApi,
  ScheduleApi,
  SpotHoldsApi,
  type ConfigurationParameters,
} from "@zipper/embedded-api-client";
import { authHeaders, getApiBase } from "../config/client";
import { embedConfigSchema } from "../config/schema";
import type { EmbedConfig } from "../config/types";
import {
  ZIPPER_EMBED_BUNDLE_ID,
  ZIPPER_EMBED_BUNDLE_VERSION,
} from "../version";

declare global {
  interface Window {
    /** Unsaved config supplied only by the admin preview host. */
    ZIPPER_EMBED_PREVIEW_CONFIG?: unknown;
  }
}

export type EmbeddedApiSurface = "public-embed" | "dashboard-customer";

/**
 * The generated SDK clients used by the embeddable surface.
 *
 * Keep construction behind this small boundary so API host selection and the
 * current embed JWT are resolved at request time. A customer can sign in after
 * the bundle has loaded, and branch/CDN previews can override the API base
 * without rebuilding the generated SDK.
 */
export interface EmbeddedApiClients {
  readonly surface: EmbeddedApiSurface;
  turnstile?: TurnstileApi;
  dashboard: DashboardApi;
  deferredMemberships: DeferredMembershipsApi;
  events: EventApi;
  eventRegister: EventRegisterApi;
  forms: FormApi;
  giftCards: GiftCardsApi;
  payments: PaymentsApi;
  publicEmbeds: PublicEmbedsApi;
  roster: RosterApi;
  schedule: ScheduleApi;
  spotHolds: SpotHoldsApi;
}

export interface CreateEmbeddedApiClientsOptions extends ConfigurationParameters {
  surface: EmbeddedApiSurface;
}

/**
 * Create a bounded set of generated clients for a host environment.
 *
 * Public embeds normally use {@link getEmbeddedApiClients}; dashboard-customer
 * can inject a same-origin set configured with cookies and its own base path.
 */
export function createEmbeddedApiClients({
  surface,
  ...configurationParameters
}: CreateEmbeddedApiClientsOptions): EmbeddedApiClients {
  const configuration = new Configuration(configurationParameters);
  return {
    surface,
    turnstile: new TurnstileApi(configuration),
    dashboard: new DashboardApi(configuration),
    deferredMemberships: new DeferredMembershipsApi(configuration),
    events: new EventApi(configuration),
    eventRegister: new EventRegisterApi(configuration),
    forms: new FormApi(configuration),
    giftCards: new GiftCardsApi(configuration),
    payments: new PaymentsApi(configuration),
    publicEmbeds: new PublicEmbedsApi(configuration),
    roster: new RosterApi(configuration),
    schedule: new ScheduleApi(configuration),
    spotHolds: new SpotHoldsApi(configuration),
  };
}

let cached:
  | {
      key: string;
      clients: EmbeddedApiClients;
    }
  | undefined;

export function getEmbeddedApiClients(orgId?: number): EmbeddedApiClients {
  const basePath = getApiBase();
  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Zipper-Client": ZIPPER_EMBED_BUNDLE_ID,
    "X-Zipper-Client-Version": ZIPPER_EMBED_BUNDLE_VERSION,
    ...(orgId ? authHeaders(orgId) : {}),
  };
  const key = `${basePath}\n${orgId ?? "anonymous"}\n${headers.Authorization ?? ""}`;
  if (cached?.key === key) return cached.clients;

  const clients = createEmbeddedApiClients({
    surface: "public-embed",
    basePath,
    headers,
  });
  cached = { key, clients };
  return clients;
}

/** HTTP status carried by a generated SDK failure, when one is available. */
export function embeddedApiErrorStatus(error: unknown): number | undefined {
  return error instanceof ResponseError ? error.response.status : undefined;
}

export class EmbedConfigError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "EmbedConfigError";
  }
}

const configCaches = new WeakMap<
  EmbeddedApiClients,
  Map<string, Promise<EmbedConfig>>
>();

/**
 * Resolve and validate an embed instance through the generated SDK.
 *
 * Abortable requests are not shared while in flight. Once resolved, their
 * value is cached like a regular request.
 */
export function fetchEmbedConfig(
  instanceId: string,
  api: EmbeddedApiClients = getEmbeddedApiClients(),
  signal?: AbortSignal,
): Promise<EmbedConfig> {
  if (
    instanceId === "preview" &&
    api.surface === "public-embed" &&
    typeof window !== "undefined" &&
    window.ZIPPER_EMBED_PREVIEW_CONFIG !== undefined
  ) {
    // Do not cache unsaved preview edits or intercept any network traffic.
    // Apply the same schema as real config; data/checkout still use the SDK.
    return Promise.resolve().then(() => {
      if (signal?.aborted) {
        throw signal.reason ?? new DOMException("Aborted", "AbortError");
      }
      const config = embedConfigSchema.parse(
        window.ZIPPER_EMBED_PREVIEW_CONFIG,
      );
      if (config.instanceId !== instanceId) {
        throw new EmbedConfigError("Preview config instance does not match");
      }
      return config;
    });
  }
  const apiCache =
    configCaches.get(api) ?? new Map<string, Promise<EmbedConfig>>();
  if (!configCaches.has(api)) configCaches.set(api, apiCache);

  const cachedConfig = apiCache.get(instanceId);
  if (cachedConfig) return cachedConfig;

  const promise = api.publicEmbeds
    .getEmbedConfig(
      { instanceId },
      signal === undefined ? undefined : { signal },
    )
    .then((config) => embedConfigSchema.parse(config))
    .catch((error: unknown) => {
      const status = embeddedApiErrorStatus(error);
      if (status === 404) {
        throw new EmbedConfigError("Embed instance not found or disabled", 404);
      }
      if (status !== undefined) {
        throw new EmbedConfigError(
          `Failed to load embed config (${status})`,
          status,
        );
      }
      throw error;
    });

  if (!signal) {
    apiCache.set(instanceId, promise);
    void promise.catch(() => apiCache.delete(instanceId));
  } else {
    void promise.then(
      (config) => apiCache.set(instanceId, Promise.resolve(config)),
      () => undefined,
    );
  }
  return promise;
}
